import axios from 'utils/axios';
import type {
    ServiceStatisticsType,
    CatalogDirActionResultType,
    GetServiceListResultType,
    ServiceCreateDataType,
    ServiceSaveDataType,
    ServiceType,
} from '@econtractor/types';

export interface ServiceListParams {
    page?: number;
    search?: string;
    order?: string;
    status?: number[];
    onPage?: number;
    Ids?: number[];
    categoryId?: number | null;
    categoryIds?: number[];
}

export interface ServiceSaveResponse {
    stat?: boolean | number;
    articleDublicate?: boolean;
}

export const getList = async (getData: ServiceListParams): Promise<GetServiceListResultType | null> => {
    try {
        const response = await axios('/services', { params: getData });
        return response?.data || null;
    } catch (e) {
        console.error(e);
    }
    return null;
};

export const getService = async (serviceId: number | string): Promise<ServiceType | null> => {
    try {
        const response = await axios(`/services/${serviceId}`);
        return response.data;
    } catch (e) {
        console.error(e);
    }
    return null;
};

export const setStat = async (aId: number | number[], status: number): Promise<boolean> => {
    try {
        const response = await axios.post('/services/status', { aId, status });
        return response.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const save = async (
    serviceId: number | string,
    saveData: ServiceCreateDataType & ServiceSaveDataType,
): Promise<ServiceSaveResponse | false> => {
    try {
        const response = await axios.post(`/services/${serviceId}`, saveData);
        return response.data || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const saveFiles = async (serviceId: number | string, servFiles: string): Promise<boolean> => {
    try {
        const response = await axios.post(`/services/${serviceId}/files`, { servFiles });
        return response.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const deleteService = async (serviceId: number | string): Promise<boolean> => {
    try {
        const response = await axios.delete(`/services/${serviceId}`);
        return response.data?.stat;
    } catch (e) {
        console.error(e);
        return false;
    }
};

const handleDirApiError = (e: unknown): CatalogDirActionResultType | null => {
    if (e && typeof e === 'object' && ('stat' in e || 'errCode' in e)) {
        return e as CatalogDirActionResultType;
    }
    if (e && typeof e === 'object' && 'response' in e) {
        const response = (e as { response?: { data?: CatalogDirActionResultType } }).response;
        if (response?.data) return response.data;
    }
    console.error(e);
    return null;
};

export const getDirUsage = async (): Promise<Record<number, number> | null> => {
    try {
        const response = await axios.get('/services/dirs/category/usage');
        return response.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createDir = async (name: string): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post('/services/dirs/category/create', { name });
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export const renameDir = async (dirId: number, name: string): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post(`/services/dirs/category/update/${dirId}`, { name });
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export const deleteDir = async (dirId: number): Promise<CatalogDirActionResultType | null> => {
    try {
        const response = await axios.post(`/services/dirs/category/delete/${dirId}`);
        return response.data;
    } catch (e) {
        return handleDirApiError(e);
    }
};

export const getStatistics = async (): Promise<ServiceStatisticsType> =>
    (await axios.get('/services/statistics')).data;
